import { createNestablePublicClientApplication } from '@azure/msal-browser';
import type { IPublicClientApplication } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { useEffect, useRef, useState } from 'react';

import { MsalAuthenticationProvider } from '../auth/msal-authentication-provider';
import { createMsalConfig, MSAL_SCOPES } from '../config/msal-config';
import { createGraphStorage } from '../storage/graph-storage';
import { AuthOptions, type AuthState } from '../types';

/**
 * MS Graph API hook with zero configuration needed.
 * All config (tenant, client ID, redirect URI, scopes) is auto-detected from environment.
 */
export function useGraphAPI() {
  const applicationRef = useRef<IPublicClientApplication | null>(null);
  const authenticationProviderRef = useRef<MsalAuthenticationProvider | null>(null);
  const [graphClient, setGraphClient] = useState<Client | null>(null);

  const storage = useRef(createGraphStorage());

  const [authState, setAuthState] = useState<AuthState>(() => storage.current.loadAuthState());

  const initOnce = async () => {
    if (applicationRef.current) {
      return;
    }

    const msalConfig = createMsalConfig();
    applicationRef.current = await createNestablePublicClientApplication(msalConfig);
    authenticationProviderRef.current = new MsalAuthenticationProvider(
      applicationRef.current,
      MSAL_SCOPES,
      msalConfig.auth.redirectUri || '',
    );
  };

  const setGraphOAuthState = (state: AuthState, setComponentState: boolean) => {
    storage.current.saveAuthState(state);

    if (setComponentState) {
      setAuthState(state);
    }
  };

  const createGraphClient = () => {
    setGraphClient(
      Client.initWithMiddleware({
        authProvider: authenticationProviderRef.current!,
      }),
    );
  };

  const login = async (): Promise<AuthState> => {
    await initOnce();

    setGraphOAuthState({ state: AuthOptions.INTERACTION_IN_PROGRESS, accessToken: null }, false);

    const state = await authenticationProviderRef.current!.login();

    setGraphOAuthState(state, true);

    if (state.state === AuthOptions.LOGGED_IN) {
      createGraphClient();
    } else {
      setGraphClient(null);
    }

    return state;
  };

  const shouldTriggerOfficeLogin = () => {
    return authState.state === AuthOptions.LOGGED_OUT || authState.state === AuthOptions.INTERACTION_REQUIRED;
  };

  const isOfficeLoginInProgress = () => {
    return authState.state === AuthOptions.INTERACTION_IN_PROGRESS;
  };

  const getAccessToken = () => {
    if (authenticationProviderRef.current && authState.state === AuthOptions.LOGGED_IN) {
      return authenticationProviderRef.current.getAccessToken();
    }

    return null;
  };

  const logout = async (): Promise<void> => {
    await initOnce();

    const accounts = applicationRef.current?.getAllAccounts();
    if (accounts && accounts.length > 0) {
      await applicationRef.current?.logoutPopup({ account: accounts[0] });
    }

    storage.current.clearAuthState();
    setAuthState({ state: AuthOptions.LOGGED_OUT });
    setGraphClient(null);
  };

  useEffect(() => {
    // Restore logged-in state after page reload
    let isMounted = true;

    if (authState.state === AuthOptions.LOGGED_IN && !graphClient) {
      const restoreLoggedInState = async () => {
        await initOnce();
        if (isMounted) {
          createGraphClient();
        }
      };

      restoreLoggedInState();
    }

    return () => {
      isMounted = false;
    };
  }, [authState.state, graphClient]);

  return {
    authState,
    login,
    logout,
    graphClient,
    shouldTriggerOfficeLogin,
    isOfficeLoginInProgress,
    getAccessToken,
  };
}
